import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import { diffAuditPayloads, formatDiffValue } from '../features/item-detail/audit-diff';
import { compareDisplayLabels } from '../lib/formatting';

const AUDIT_PAGE_SIZE = 25;
const MASKED_FIELDS = new Set(['identity', 'notes', 'attentionReason', 'maintenanceNotes']);

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Invalid timestamp'
    : `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

function visibleAuditValue(field: string, value: unknown): string {
  return MASKED_FIELDS.has(field) ? 'Hidden in global history' : formatDiffValue(value);
}

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const operation = params.get('operation') ?? '';
  const dateFrom = params.get('from') ?? '';
  const dateTo = params.get('to') ?? '';
  const filterKey = `${query}|${operation}|${dateFrom}|${dateTo}`;
  const [tokens, setTokens] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setTokens([undefined]);
    setPageIndex(0);
  }, [filterKey]);

  const pageToken = tokens[pageIndex];
  const history = useQuery({
    queryKey: ['audit', { filterKey, pageToken }],
    queryFn: () =>
      inventoryApi.listAuditEvents({
        ...(query.trim() ? { query: query.trim() } : {}),
        ...(operation ? { operation } : {}),
        ...(dateFrom ? { dateFrom: new Date(`${dateFrom}T00:00:00.000Z`).toISOString() } : {}),
        ...(dateTo ? { dateTo: new Date(`${dateTo}T23:59:59.999Z`).toISOString() } : {}),
        pageSize: AUDIT_PAGE_SIZE,
        ...(pageToken ? { pageToken } : {}),
      }),
  });

  const events = useMemo(() => history.data?.data ?? [], [history.data?.data]);
  const operations = useMemo(
    () =>
      [...new Set(['CREATE', 'UPDATE', 'DELETE', ...events.map((event) => event.operationType)])]
        .filter(Boolean)
        .sort(compareDisplayLabels),
    [events],
  );

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  }

  function goToNextPage(): void {
    const nextToken = history.data?.nextPageToken;
    if (!nextToken) return;
    setTokens((current) => {
      const copy = [...current];
      copy[pageIndex + 1] = nextToken;
      return copy;
    });
    setPageIndex((index) => index + 1);
  }

  function goToPreviousPage(): void {
    setPageIndex((index) => Math.max(0, index - 1));
  }

  return (
    <>
      <PageHeader
        title="Audit history"
        description="Item changes by time, operation, and actor."
        actions={<p className="provenance-note">Sensitive fields are masked.</p>}
      />

      <form className="audit-filter-bar" onSubmit={(event) => event.preventDefault()}>
        <label className="search-field">
          <span>Item, operation, or actor</span>
          <input
            type="search"
            value={query}
            onChange={(event) => updateParam('q', event.target.value)}
            placeholder="Search item ID, operation, or actor"
          />
        </label>
        <label>
          Operation
          <SearchableSelect
            ariaLabel="Operation"
            onChange={(value) => updateParam('operation', value)}
            options={[
              { value: '', label: 'All operations' },
              ...operations.map((value) => ({ value, label: value })),
            ]}
            placeholder="All operations"
            value={operation}
          />
        </label>
        <label>
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => updateParam('from', event.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={dateTo}
            onChange={(event) => updateParam('to', event.target.value)}
          />
        </label>
        <button type="button" onClick={() => setParams(new URLSearchParams())}>
          Clear filters
        </button>
      </form>

      {history.isLoading && events.length === 0 ? (
        <PageLoadingState label="Loading audit history…" className="page-loading-inline" />
      ) : null}
      {history.isError ? (
        <ErrorState
          title="Audit history could not be loaded"
          rpcName="ListAuditEvents"
          onRetry={() => void history.refetch()}
        />
      ) : null}

      {!history.isLoading && !history.isError && events.length === 0 ? (
        <div className="empty-state">
          <strong>No audit events match these filters.</strong>
          <p className="empty-note">
            Try a wider date range or clear the item and operation filters.
          </p>
          <button type="button" onClick={() => setParams(new URLSearchParams())}>
            Clear filters
          </button>
        </div>
      ) : null}

      {events.length > 0 ? (
        <section className="audit-feed" aria-label="Immutable item audit events">
          <div className="audit-feed-heading">
            <strong>{events.length.toLocaleString()} events loaded</strong>
            <span>Newest first · UTC</span>
          </div>
          {events.map((event) => {
            const diffs = diffAuditPayloads(event.old, event.new);
            return (
              <details className="audit-feed-event" key={event.auditId}>
                <summary>
                  <span
                    className={`operation-badge operation-${event.operationType.toLowerCase()}`}
                  >
                    {event.operationType}
                  </span>
                  <Link to={`/inventory/${encodeURIComponent(event.recordId)}`} className="mono">
                    {event.recordId}
                  </Link>
                  <span>{event.changedBy ?? 'Unknown actor'}</span>
                  <time dateTime={event.changedAt} className="mono">
                    {formatDateTime(event.changedAt)}
                  </time>
                  <span className="audit-expand-hint">{diffs.length} fields ▾</span>
                </summary>
                <div className="audit-feed-detail">
                  {diffs.length > 0 ? (
                    <dl className="audit-diff-grid">
                      {diffs.map((diff) => (
                        <div className="audit-diff-row" key={diff.field}>
                          <dt>{diff.field}</dt>
                          <dd>
                            <span className="audit-diff-old">
                              {visibleAuditValue(diff.field, diff.oldValue)}
                            </span>
                            {' to '}
                            <span className="audit-diff-new">
                              {visibleAuditValue(diff.field, diff.newValue)}
                            </span>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="empty-note">No field changes available.</p>
                  )}
                  <span className="mono audit-id">Audit ID {event.auditId}</span>
                </div>
              </details>
            );
          })}
        </section>
      ) : null}

      {events.length > 0 ? (
        <nav className="page-controls" aria-label="Audit pagination">
          <span>
            {(pageIndex * AUDIT_PAGE_SIZE + 1).toLocaleString()}–
            {(pageIndex * AUDIT_PAGE_SIZE + events.length).toLocaleString()} of{' '}
            {(history.data?.total ?? events.length).toLocaleString()}
          </span>
          <div>
            <button type="button" onClick={goToPreviousPage} disabled={pageIndex === 0}>
              Previous
            </button>
            <span>Page {(pageIndex + 1).toLocaleString()}</span>
            <button type="button" onClick={goToNextPage} disabled={!history.data?.nextPageToken}>
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </>
  );
}
